import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd-next";
import { ExpandLess, ExpandMore, MoreVertOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  createTaskCrewAction,
  resetTaskCrewStateAction,
} from "../../../../store/reducers/assignWorkerSlice";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import dayjs from "dayjs";

/**
 * Types
 */
type Item = {
  id: string;
  content: string;
  customer_name?: string;
  type?: string; // e.g. 'vehicle' | 'driver' | 'task' | 'portal' | 'unavailable' | 'inYard'
  start_time?: string;
  end_time?: string;
  fromPostCode?: string;
  toPostCode?: string;
  foreman_id?: string;
  skills?: { name: string }[]; // <-- Added this line
};

type ColumnData = {
  id?: string; // crew id
  name: string;
  section: "tabs" | "status" | "crew";
  foreman_id?: string; // only for crew columns
  items: Item[];
};

type ColumnsState = Record<string, ColumnData>;

/**
 * Column component.
 *
 * Important: The Droppable is always rendered (even if collapsed) so the drag lifecycle
 * won't lose the DOM node when a column collapses while dragging. We simply hide the
 * *items list* visually when collapsed.
 */
const Column = ({
  colId,
  column,
  setColumns,
  md,
}: {
  colId: string;
  column: ColumnData;
  setColumns: React.Dispatch<React.SetStateAction<ColumnsState>>;
  md?: number;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedColId, setSelectedColId] = useState<string | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    colId: string,
    itemId: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemId(itemId);
    setSelectedColId(colId);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleMarkForeman = () => {
    setConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmYes = () => {
    if (!selectedColId || !selectedItemId) return;

    setColumns((prev: any) => {
      const col = prev[selectedColId];
      if (!col) return prev;

      // Set the column's foreman_id to the selected item
      return {
        ...prev,
        [selectedColId]: {
          ...col,
          foreman_id: selectedItemId,
        },
      };
    });

    console.log("column", column);

    // Reset selection & close menu
    setConfirmOpen(false);
    setAnchorEl(null);
    setSelectedItemId(null);
    setSelectedColId(null);
  };
console.log("column-125", column);
  return (
    <>
      {confirmOpen && (
        <AlertPopup
          open={confirmOpen}
          title="Confirm Team Leader"
          content="Are you sure you want to mark this worker as Team Leader?"
          isError={false}
          onHide={() => setConfirmOpen(false)}
          submitHandler={handleConfirmYes}
        />
      )}
      <Grid
        item
        xs={md ?? 4}
        sx={{
          borderTopRightRadius: "5px",
          borderTopLeftRadius: "5px",
          padding: 0,
          height: "auto",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setCollapsed((v) => !v)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#02906f",
            color: "white",
            p: "4px 8px",
            borderTopRightRadius: "5px",
            borderTopLeftRadius: "5px",
            cursor: "pointer",
            height: 24,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontSize: 15,whiteSpace:'nowrap' }}>
            {column.name}
          </Typography>
          <IconButton
            size="small"
            sx={{ color: "white" }}
            aria-label={`${column.name}-toggle`}
          >
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>

        {/* Droppable is always present — only items rendering toggles on collapse */}
        <Droppable droppableId={colId}>
          {(provided: any, snapshot: any) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                minHeight: collapsed ? 0 : 120,
                padding: "6px",
                background: snapshot.isDraggingOver
                  ? "rgba(0,112,96,0.12)"
                  : "rgba(224,224,224,0.6)",
                marginBottom: "8px",
                border: "1px solid #C4CDD5",
                transition: "background .15s ease",
              }}
            >
              {/* If collapsed -> show a concise placeholder but keep droppable in DOM */}
              {collapsed ? (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", p: 1 }}
                >
                  {column.items.length} item(s)
                </Typography>
              ) : (
                column.items.map((item: Item, index: number) => {
                  if (!item.id || typeof item.id !== "string" || item.id.trim() === "") {
                    console.warn("Skipping item with invalid id in Draggable:", item);
                    return null;
                  }
                  return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(providedDr: any, snapshotDr: any) => (
                      <Card
                        ref={providedDr.innerRef}
                        {...providedDr.draggableProps}
                        {...providedDr.dragHandleProps}
                        sx={{
                          mb: 1,
                          cursor: "grab",
                          width: "100%",
                          background: "white",
                          border: "1px solid #B0B0B0",
                          boxShadow: snapshotDr.isDragging
                            ? "0 0 12px rgba(0,0,0,0.35)"
                            : "1px 1px 6px rgba(0,0,0,0.12)",
                        }}
                      >
                        <CardContent sx={{ p: "8px !important" }}>
                          {item.type?.toUpperCase() !== "TASK" && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  color: "#333",
                                  fontWeight: 500,
                                }}
                              >
                                {(() => {
                                  const hasDriver = item?.skills?.some(
                                    (s) => s.name.toUpperCase() === "DRIVER",
                                  );
                                  const hasPorter = item?.skills?.some(
                                    (s) =>
                                      s.name.toUpperCase() === "PORTER" ||
                                      s.name.toUpperCase() === "SKILLED PORTER",
                                  );

                                  if (hasDriver && hasPorter) return "[D] [P]";
                                  if (hasDriver) return "[D]";
                                  if (hasPorter) return "[P]";

                                  // fallback: check by type
                                  switch (item?.type?.toUpperCase()) {
                                    case "PORTER":
                                      return "[P]";
                                    case "DRIVER":
                                      return "[D]";
                                    case "VEHICLE":
                                      return "[V]";
                                    default:
                                      return "";
                                  }
                                })()}{" "}
                                {item.content}
                                {column.foreman_id === item.id && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      color: "green",
                                      fontWeight: "bold",
                                      fontSize: 12,
                                    }}
                                  >
                                    [TL]
                                  </Typography>
                                )}
                              </Typography>
                              {/* ✅ Foreman checkbox only for PORTER / DRIVER */}
                              {column?.section === "crew" &&
                                ["PORTER", "DRIVER"].includes(
                                  item.type?.toUpperCase() || "",
                                ) && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleMenuOpen(e, colId, item.id)
                                      }
                                    >
                                      <MoreVertOutlined fontSize="small" />
                                    </IconButton>

                                    {/* Menu */}
                                    <Menu
                                      anchorEl={anchorEl}
                                      open={Boolean(anchorEl)}
                                      onClose={handleMenuClose}
                                    >
                                      <MenuItem onClick={handleMarkForeman}>
                                        Mark as Team Leader
                                      </MenuItem>
                                    </Menu>
                                  </>
                                )}
                            </Box>
                          )}
                          {item.type?.toUpperCase() === "TASK" && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "text.secondary",
                                fontWeight: "bold",
                              }}
                            >
                              <Box
                                sx={{
                                  textAlign: "center",
                                  background: "#E5F4F1",
                                }}
                              >
                                {item?.start_time}
                              </Box>
                              <Box sx={{ textAlign: "left" }}>
                                {item.content}{" "}
                              </Box>
                              {item?.customer_name && (
                                <Box sx={{ textAlign: "left" }}>
                                  ({item?.customer_name})
                                </Box>
                              )}
                              {item?.fromPostCode && (
                                <Box sx={{ textAlign: "left" }}>
                                  {item?.fromPostCode}
                                </Box>
                              )}
                              {item?.toPostCode && (
                                <Box sx={{ textAlign: "left" }}>
                                  {item?.toPostCode}
                                </Box>
                              )}
                              <Box
                                sx={{
                                  textAlign: "center",
                                  background: "#E5F4F1",
                                }}
                              >
                                {item?.end_time}
                              </Box>
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                )
              }
              )
              )}
            </Box>
          )}
        </Droppable>
      </Grid>
    </>
  );
};

/**
 * Helper: reorder items within same list
 */
const reorder = (list: Item[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/**
 * Helper: move item from one list to another
 */
const moveItem = (
  source: Item[],
  destination: Item[],
  sourceIndex: number,
  destIndex: number,
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [moved] = sourceClone.splice(sourceIndex, 1);
  destClone.splice(destIndex, 0, moved);
  return { source: sourceClone, dest: destClone, moved };
};

const buildPayload = (columns: ColumnsState, filteredData: any) => {
  const nowIso = filteredData?.date;

  const crews = Object.entries(columns)
    .filter(([_, c]) => c.section === "crew" &&
      c.items.length > 0, // only include crews with at least 1 item
    )
    .map(([colId, c]) => {
      // assets are items typed as 'vehicle' (or 'porter', 'driver), tasks typed as 'task'
      const assets = c.items
        .filter(
          (it) =>
            it.type?.toUpperCase() === "VEHICLE" ||
            it.type?.toUpperCase() === "DRIVER" ||
            it.type?.toUpperCase() === "PORTER",
        )
        .map((it) => ({ id: it.id, type: it.type || "VEHICLE" }));

      const tasks = c.items
        .filter((it) => it.type?.toUpperCase() === "TASK")
        .map((it) => ({ id: it.id, name: it.content }));
      return {
        id: c.id || null,
        name: c.name,
        date: nowIso,
        foreman_id: c.foreman_id || null,
        assets,
        tasks,
      };
    });

  // status buckets - uses expected keys 'unavailable' and 'inYard' from your example payload
  const unavailable = (columns.unavailable?.items ?? []).map((it) => ({
    id: it.id,
    type: it.type ?? "unavailable",
    date: nowIso,
  }));

  const inyard = (columns.inYard?.items ?? []).map((it) => ({
    id: it.id,
    type: it.type ?? "inYard",
    date: nowIso,
  }));

  return { crews, unavailable, inyard };
};

/**
 * Main component
 */
const CrewBoard = ({
  filteredData,
  isEditable,
  isViewOnly,
}: {
  filteredData: any;
  isEditable?: boolean;
  isViewOnly?: boolean;
}): any => {
  const { taskCrewList, operationTasks } = useSelector(
    (state: any) => state.assignWorker,
  );
  const crewScrollRef = useRef<HTMLDivElement | null>(null);

  const handleCrewScroll = (direction: "left" | "right") => {
    if (crewScrollRef.current) {
      crewScrollRef.current.scrollBy({
        left: direction === "left" ? -400 : 400,
        behavior: "smooth",
      });
    }
  };
  const [columns, setColumns] = useState<ColumnsState>(taskCrewList);
  const [initialColumns, setInitialColumns] = useState<ColumnsState>(taskCrewList);
  const [isDragging, setIsDragging] = useState(false);
  const [driverValidationOpen, setDriverValidationOpen] = useState(false);
  const [driverValidationMessage, setDriverValidationMessage] = useState("");
  const dispatch = useDispatch();
  console.log("taskCrewList-24", taskCrewList);
  useEffect(() => {
    // Only update columns if not dragging
    if (!isDragging) {
      setColumns(taskCrewList);
      setInitialColumns(taskCrewList); // ✅ keep original copy for discard
    }
  }, [taskCrewList, isDragging]);

  // ✅ Helper: check if all crews have at least one worker
  const shouldAddNewCrew = (cols: ColumnsState) => {
    // Add new crew only if last crew has any worker/porter/vehicle
    const crewCols = Object.values(cols).filter((c) => c.section === "crew");
    if (!crewCols.length) return true;
    const lastCrew = crewCols[crewCols.length - 1];
    return lastCrew.items.some((it) =>
      ["VEHICLE", "DRIVER", "PORTER"].includes(it.type?.toUpperCase() || ""),
    );
  };

  const getCrewDriverVehicleValidationErrors = (cols: ColumnsState) => {
    return Object.values(cols)
      .filter((c) => c.section === "crew")
      .map((crew) => {
        const vehicleCount = crew.items.filter(
          (it) => it.type?.toUpperCase() === "VEHICLE",
        ).length;

        const driverCount = crew.items.filter((it) => {
          if (it.type?.toUpperCase() === "DRIVER") return true;
          return (
            it.skills?.some((s) => s.name?.toUpperCase() === "DRIVER") ?? false
          );
        }).length;

        return {
          crewName: crew.name,
          vehicleCount,
          driverCount,
          isInvalid: vehicleCount > driverCount,
        };
      })
      .filter((x) => x.isInvalid);
  };

  const isPastDate =
    filteredData?.date && dayjs(filteredData.date).isBefore(dayjs().subtract(60, "day"), "day");
  const finalIsEditable = isEditable && !isViewOnly && !isPastDate;

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (!finalIsEditable) return;
    if (!result.destination) return;
    const { source, destination } = result;
    const sourceId = source.droppableId;
    const destId = destination.droppableId;
    const draggedItem = columns[sourceId].items[source.index];
    const itemType = draggedItem.type?.toUpperCase();
    // defensive checks
    if (!columns[sourceId] || !columns[destId]) return;

    // 🚫 Restrict moves between Vehicle / Driver / Porter base columns
    const isRestricted =
      (sourceId === "vehicles" &&
        (destId === "drivers" || destId === "portal")) ||
      (sourceId === "drivers" &&
        (destId === "vehicles" || destId === "portal")) ||
      (sourceId === "portal" &&
        (destId === "vehicles" || destId === "drivers"));
    if (isRestricted) {
      alert(
        `❌ Blocked: cannot move ${itemType} from ${sourceId} to ${destId}`,
      );
      return;
    }

    // 🚫 Extra rule: when moving to crew sections, allow only matching type
    const baseSections = ["vehicles", "drivers", "portal"];
    if (baseSections.includes(destId)) {
      // Map base droppableId to allowed itemType
      const allowedType: Record<string, string> = {
        vehicles: "VEHICLE",
        drivers: "DRIVER",
        portal: "PORTER",
      };

      if (itemType !== allowedType[destId]) {
        alert(
          `❌ Blocked: ${itemType} cannot be assigned to ${
            destId === "portal" ? "Porter" : destId
          }`,
        );
        return;
      }
    }

    // 🚫 New restriction: prevent moving crew to "unavailable" or "inYard"
    const forbiddenDestinations = ["unavailable", "inYard"];
    if (
      sourceId?.toLowerCase().startsWith("crew") &&
      forbiddenDestinations.includes(destId)
    ) {
      alert(`❌ Blocked: ${sourceId} cannot be moved to ${destId}`);
      return;
    }

    // same column => reorder
    if (sourceId === destId) {
      const newItems = reorder(
        columns[sourceId].items,
        source.index,
        destination.index,
      );
      const newColumns = {
        ...columns,
        [sourceId]: { ...columns[sourceId], items: newItems },
      };
      setColumns(newColumns);
      return;
    }

    // different columns => move
    const { source: newSourceItems, dest: newDestItems } = moveItem(
      columns[sourceId].items,
      columns[destId].items,
      source.index,
      destination.index,
    );

    // Update columns state
    const updatedColumns: ColumnsState = { ...columns };
    updatedColumns[sourceId] = { ...columns[sourceId], items: newSourceItems };
    updatedColumns[destId] = { ...columns[destId], items: newDestItems };

    // Add new blank crew if needed
    if (shouldAddNewCrew(updatedColumns)) {
       const crewNumbers = Object.keys(updatedColumns)
    .filter((key) => key.startsWith("crew"))
    .map((key) => parseInt(key.replace("crew", ""), 10))
    .filter((n) => !isNaN(n));

  const nextCrewNo =
    crewNumbers.length > 0
      ? Math.max(...crewNumbers) + 1
      : 1;
      const newCrewKey = `crew${nextCrewNo}`;
      if (!updatedColumns[newCrewKey]) {
        updatedColumns[newCrewKey] = {
          name: `Crew ${nextCrewNo}`,
          section: "crew",
          foreman_id: "",
          items: [],
        };
      }
    }
    setColumns(updatedColumns);
  };

  // Example: send payload to API - replace URL and method as needed
  const sendPayload = async () => {
    if (isViewOnly) return;

    const driverVehicleErrors = getCrewDriverVehicleValidationErrors(columns);
    if (driverVehicleErrors.length) {
      const message = driverVehicleErrors
        .map(
          (x) =>
            `${x.crewName}: ${x.vehicleCount} vehicle(s), ${x.driverCount} driver(s)`,
        )
        .join("\n");
      setDriverValidationMessage(
        `Each crew must have at least as many drivers as vehicles.\n\n${message}`,
      );
      setDriverValidationOpen(true);
      return;
    }

    const payload = buildPayload(columns, filteredData);
 
    dispatch(createTaskCrewAction(payload));
  };

  console.log("column-644",
  Object.entries(columns)
    .filter(([_, col]) => col.section === "crew")
    .map(([k, c]) => ({
      crew: k,
      items: c.items.length,
      validItems: c.items.filter(i => i.id).length,
    }))
);

  return (
    <>
      <Dialog
        open={driverValidationOpen}
        onClose={() => setDriverValidationOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Driver Assignment Validation</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: "pre-line", pt: 1 }}>
            {driverValidationMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDriverValidationOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {operationTasks && operationTasks?.length === 0 ? (
        <Typography
          variant="h6"
          color="error"
          fontWeight={600}
          align="center"
          sx={{ margin: "20px 0px" }}
        >
          No Crew Available for this Date.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 8px",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Crew Board
            </Typography>
            <Box>
              <Button
                disabled={!finalIsEditable}
                variant="outlined"
                color="error"
                sx={{ mr: 1 }}
                onClick={() => {
                  setColumns(initialColumns); // revert all changes
                }}
              >
                Discard
              </Button>
              {/* <Button
                disabled={!isEditable}
                variant="outlined"
                sx={{ mr: 1 }}
                onClick={() => dispatch(addBlankCrewAction())} // 👈 add crew
              >
                Add Crew
              </Button> */}
              <Button
                disabled={!finalIsEditable}
                variant="contained"
                onClick={sendPayload}
                sx={{ mr: 1 }}
              >
                Save
              </Button>
              <Button
                disabled={!finalIsEditable}
                variant="outlined"
                onClick={() => {
                  dispatch(
                    resetTaskCrewStateAction({
                      date: filteredData?.date,
                      reset_all: true,
                    }),
                  );
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          <DragDropContext
            key={filteredData?.date + JSON.stringify(Object.keys(columns))}
            onDragEnd={onDragEnd}
          >
            <Grid container spacing={2} sx={{ padding: "16px 0px" }}>
              {/* Section 1: Tabs (left column) */}
              <Grid
                item
                xs={2}
                sx={{
                  // vertical scroll
                  maxHeight: "80vh", // or any height you want
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <Grid
                  container
                  spacing={0.5}
                  sx={{
                    pointerEvents: finalIsEditable ? "auto" : "none",
                    opacity: finalIsEditable ? 1 : 0.6,
                  }}
                >
                  {Object.entries(columns)
                    .filter(([_, col]) => col.section === "tabs")
                    .map(([colId, column]) => (
                      <Column
                        key={colId}
                        colId={colId}
                        column={column}
                        setColumns={setColumns}
                        md={12}
                      />
                    ))}
                </Grid>
              </Grid>

              {/* Section 2: Status */}
              <Grid item xs={2}>
                <Grid
                  container
                  spacing={0.5}
                  sx={{
                    pointerEvents: finalIsEditable ? "auto" : "none",
                    opacity: finalIsEditable ? 1 : 0.6,
                  }}
                >
                  {Object.entries(columns)
                    .filter(([_, col]) => col.section === "status")
                    .map(([colId, column]) => (
                      <Column
                        key={colId}
                        colId={colId}
                        column={column}
                        setColumns={setColumns}
                        md={12}
                      />
                    ))}
                </Grid>
              </Grid>

              {/* Section 3: Crew */}
              <Grid
                item
                xs={8}
                sx={{
                  overflowX: "auto",
                }}
              >
                {/* Top Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    paddingBottom: "4px",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleCrewScroll("left")}
                  >
                    Prev
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCrewScroll("right")}
                  >
                    Next
                  </Button>
                </Box>

                {/* Horizontal Scroll Area */}
                <Box
                  ref={crewScrollRef}
                  sx={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    width: "100%",
                    scrollBehavior: "smooth",

                    "&::-webkit-scrollbar": {
                      height: 8,
                    },

                    "&::-webkit-scrollbar-thumb": {
                      background: "#bdbdbd",
                      borderRadius: "10px",
                    },
                    
                  }}
                >
                  <Grid
                    container
                    spacing={0.5}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "nowrap", // ❌ prevent wrapping
                      overflowX: "auto", // ✅ enable horizontal scroll
                      scrollbarWidth: "thin",
                      minWidth: "max-content",
                      "&::-webkit-scrollbar": { height: 8 },
                      pointerEvents: finalIsEditable ? "auto" : "none",
                      opacity: finalIsEditable ? 1 : 0.6,
                    }}
                  >
                    {Object.entries(columns)
                      .filter(([_, col]) => col.section === "crew")
                      .map(([colId, column]) => (
                        <Column
                          key={colId}
                          colId={colId}
                          column={column}
                          setColumns={setColumns}
                          md={4}
                        />
                      ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </DragDropContext>
        </>
      )}
    </>
  );
};

export default CrewBoard;
